"use client";

import { useEffect, useState } from "react";
import { getEmployerContactOptions } from "@/app/actions/job-eligibility";
import { evaluateEligibility } from "@/lib/marketplace/eligibility";
import { EligibilitySummary } from "./EligibilitySummary";

export function useEmployerContactRequirements(candidateId: string | null) {
  const [result, setResult] = useState<(Awaited<ReturnType<typeof getEmployerContactOptions>> & { candidateId: string }) | null>(null);
  const [listingId, setListingId] = useState("");
  useEffect(() => {
    if (!candidateId) return;
    let cancelled = false;
    getEmployerContactOptions(candidateId).then((data) => {
      if (cancelled) return;
      setResult({ ...data, candidateId });
      setListingId(data.listings.length === 1 ? data.listings[0].id : "");
    }).catch(() => {
      if (!cancelled) setResult({ candidateId, listings: [], error: "Could not check the job requirements. Please reopen this message." });
    });
    return () => { cancelled = true; };
  }, [candidateId]);

  const current = result?.candidateId === candidateId ? result : null;
  const listing = current?.listings.find((item) => item.id === listingId);
  const assessment = listing ? evaluateEligibility(listing.eligibility_requirements, current?.profile, listing.start_date) : null;
  const error = !current
    ? "Checking job requirements..."
    : current.error || (current.listings.length && !listing ? "Choose the job you are contacting this candidate about." : assessment?.status === "not_eligible" ? "This candidate does not currently meet the essential requirements for this role." : null);
  return { listingId, setListingId, listing, assessment, current, error, loading: Boolean(candidateId && !current) };
}

export function EmployerContactRequirements({ contact }: { contact: ReturnType<typeof useEmployerContactRequirements> }) {
  return (
    <div className="mb-4 space-y-3">
      {contact.loading && <p role="status" className="text-sm">Checking job requirements...</p>}
      {contact.current?.error && <p role="alert" className="text-sm text-error">{contact.current.error}</p>}
      {!!contact.current?.listings.length && (
        <label className="block text-sm">
          Job listing
          <select className="min-h-11 w-full rounded-lg border border-border bg-white px-3" value={contact.listingId} onChange={(event) => contact.setListingId(event.target.value)}>
            <option value="">Choose a job</option>
            {contact.current.listings.map((listing) => <option key={listing.id} value={listing.id}>{listing.title}</option>)}
          </select>
        </label>
      )}
      {contact.listing && <EligibilitySummary requirements={contact.listing.eligibility_requirements} profile={contact.current?.profile} startDate={contact.listing.start_date} />}
      {contact.assessment?.status === "needs_details" && <p className="text-sm text-text-muted">Some details are missing. Use your message to clarify these before discussing an offer.</p>}
    </div>
  );
}
